import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { ListContactsComponent } from '../../../../shared/contact/list-contacts.component';
import { BPProfilesProductCatalogDataService, IMasterCatalogDetail } from './bp-profiles-product-catalog-data.service';
import { StateService } from '@uirouter/angular';
import { DialogSize, GaMessageService, IDialog } from '../../../../shared/message/ga-message.service';
import { GA1Service } from '../../../../shared/ga1/ga1.service';
import * as moment from 'moment';
import { Moment } from 'moment';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { util } from '../../../../shared/util';
import { IObject } from '../../../../../type_definitions/datatypes';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { IUserData } from '../../../../shared/services/auth/auth-data.service';
import { SEC_BP_PRODUCT_CATALOG } from '../../../../shared/services/auth/security-objects';

@Component({
  selector: 'bp-profiles-product-catalog',
  template: `
    <h2 *ngIf="loading"><activity-indicator [useDefaultText]="true"></activity-indicator></h2>
    
    <div class="bpprofiles-product-catalog" *ngIf="!loading && model">
      <h2>Product Catalog</h2>
      
      <button class="btn btn-primary col-space-bottom" (click)="upload()" *ngIf="hasWriteAccess">Upload</button>
      
      <div class="col-space-top margin-top-sm">
        <formly [form]="form" [fields]="fields" [model]="model"></formly>  
      </div>
      
      <div class="col-space-top margin-top-sm" *ngIf="model?.catalogId && model?.webshopId">
        <a [href]="catalogProductsUrl" target="_blank">Click here to see catalog contents</a>
      </div>
    </div>
  `
})
export class BPProfilesProductCatalogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields: IExtraFieldConfigurationObject[];
  public model: IMasterCatalogDetail;
  public loading: boolean;
  public hasWriteAccess: boolean;

  private profileId: string;
  private validityDateField: IExtraFieldConfigurationObject;

  private eventHandler: Subscription;
  private uploadDialog: IDialog;
  private currentUser: IUserData;

  constructor(
    private formlyService: GaFormlyService,
    private formattingService: FormattingService,
    private bpProductCatalogDataService: BPProfilesProductCatalogDataService,
    private stateService: StateService,
    private messageService: GaMessageService,
    private ga1Service: GA1Service,
    private gaEventService: GaEventService,
    private authService: AuthService
  ) {
    /**/
  }

  public ngOnInit(): void {
    this.profileId = this.stateService.params.profileId;
    this.currentUser = this.authService.getCurrentUser();
    this.hasWriteAccess = this.authService.viewWrite(SEC_BP_PRODUCT_CATALOG);

    // load catalog data
    this.loadCatalogData();

    // register listener
    this.listen();
  }

  public ngOnDestroy(): void {
    this.eventHandler.unsubscribe();
  }

  private listen(): void {
    this.eventHandler = this.gaEventService.events.subscribe((event: IGAEvent) => {
      if (event.name === EVENT_CLOSE_DIALOG) {
        this.uploadDialog.hide();
      }
    });
  }

  public get catalogProductsUrl(): string {
    return `/ga2/#/unacc?menu=WEBSHOP_${this.model?.webshopId}&url=%2Funacc%2FWP_CatalogView_Browse.jsp%3FDesprid%3D${this.model?.webshopId}%26Decatid%3D${this.model?.catalogId}`;
  }

  private loadCatalogData = (): void => {
    // fetch master catalog detail
    this.loading = true;
    this.bpProductCatalogDataService.getMasterCatalogDetail(this.profileId)
      .then((res: IMasterCatalogDetail) => {
        this.model = res;

        // create fields
        this.fields = this.createFields();

        // compare validity date
        this.compareValidityDate();
      })
      .finally(() => {
        this.loading = false;
      });
  };

  public upload(): void {
    const ga1UrlOptions: IObject = {
      ejbEvent: 'create',
      browseList: true,
      Deimpty: 'CATALOGUE',
      Desprid: this.model?.webshopId, // pre-filled webshop
      Derecid: this.model?.catalogId, // pre-filled catalog id
      bpMasterCatalog: true,
      radDerecid: this.isNewCatalog ? 1 : 0 // pre-filled radio button of update existing catalog or create new one
    };

    if (this.isNewCatalog) {
      ga1UrlOptions.ownerCompanyId = this.currentUser?.location?.companyCountry?.company?.id; // pre-filled owner company
      ga1UrlOptions.ownerContactId = this.currentUser?.id; // pre-filled owner contact
    }

    const ga1Url: string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_UPLOAD_CATALOG, ga1UrlOptions);
    this.uploadDialog = this.messageService.showGA1Dialog('Upload Catalog', ga1Url, { size: DialogSize.XL });

    // on upload done
    this.uploadDialog.onHide.subscribe(this.loadCatalogData);
  }

  private get isNewCatalog(): boolean {
    return util.isNullOrUndefinedOrEmpty(this.model?.catalogId);
  }

  private compareValidityDate(): void {
    const sourceDate: Moment = moment();
    const targetDate: Moment = moment(this.model.validityDate);

    if (sourceDate < targetDate) {
      this.validityDateField.templateOptions.valueClass = this.validityDateField.templateOptions.valueClass?.indexOf('text-success') > -1 ?
        this.validityDateField.templateOptions.valueClass : `${this.validityDateField.templateOptions.valueClass} text-success`;
    } else {
      this.validityDateField.templateOptions.valueClass = this.validityDateField.templateOptions.valueClass?.indexOf('text-danger') > -1 ?
        this.validityDateField.templateOptions.valueClass : `${this.validityDateField.templateOptions.valueClass} text-danger`;
    }
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    const labelClass: string = 'col-sm-4';
    const valueClass: string = 'col-sm-8';

    return [
      this.formlyService.aValue('title')
        .withValueClass(valueClass)
        .withLabelClass(labelClass)
        .build(),
      this.formlyService.aValue('totalProducts')
        .withValueClass(valueClass)
        .withLabelClass(labelClass)
        .build(),
      this.validityDateField = this.formlyService.aValue('validityDate')
        .withFormat(this.formattingService.formatDate)
        .withValueClass(valueClass)
        .withLabelClass(labelClass)
        .build(),
      this.formlyService.aCustom('BPPROFILES.PRODUCT_CATALOG.OWNER.LABEL')
        .withComponent(ListContactsComponent)
        .withWrapperClass('owner-contact')
        .withLabelClass(labelClass)
        .withValueClass(`${valueClass} value`)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              contacts: [this.model.owner]
            }
          }
        })
        .withKey('OWNER')
        .build(),
      this.formlyService.aValue('modifiedBy')
        .withFormat(this.formattingService.formatContact)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aValue('modificationDate')
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    ];
  }
}